/**
 * Conversation Utilities
 * 
 * Utilities for working with conversation IDs and validation.
 */

/**
 * Utility function to check if a string is a valid conversation ID (UUID format)
 * @param id - The ID to validate
 * @returns boolean - True if the ID is a valid UUID format
 */
export const isValidConversationId = (id: string | null | undefined): boolean => {
  if (!id) return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
};

/**
 * Utility function to get the best conversation ID from available options
 * Prioritizes valid UUIDs over timestamps or other formats
 * @param ids - Array of potential conversation IDs
 * @returns string | null - The best conversation ID or null if none found
 */
export const getBestConversationId = (...ids: (string | null | undefined)[]): string | null => {
  // Find the first ID that looks like a valid UUID
  for (const id of ids) {
    if (isValidConversationId(id)) {
      return id as string;
    }
  }
  // If no valid UUID found, return the first non-empty ID (might be timestamp)
  return ids.find(id => id && id.trim()) as string || null;
};
